// Self-contained trip-page and profile panels extracted from App.jsx as a
// first cut at splitting the monolith. Adding the older heavier components
// (TimelineView, FlightDetailCard, AddSegmentModal, ProfileView) here is
// straightforward but they carry larger dependency surfaces, so they stay in
// App.jsx for now.

import React, { useState, useEffect, useRef, useCallback } from "react";
import { enablePushNotifications, disablePushNotifications } from "./push";
import { partitionAttachments } from "./attachment-utils.js";

// `<img src="/api/...">` and `<a href="/api/...">` send no Authorization
// header, so authenticated attachment routes return 401. Pull the bytes via
// authFetch and hand the browser a blob: URL instead.
function AuthImage({ src, authFetch, alt, style }) {
  const [url, setUrl] = useState(null);
  // authFetch is unstable across renders by design; including it would
  // re-fetch every parent render. src is the real dependency.
  useEffect(() => {
    let cancelled = false; let created = null;
    authFetch(src).then(r => (r && r.ok) ? r.blob() : null).then(b => {
      if (cancelled || !b) return;
      created = URL.createObjectURL(b);
      setUrl(created);
    }).catch(() => {});
    return () => { cancelled = true; if (created) URL.revokeObjectURL(created); };
  }, [src]); // eslint-disable-line react-hooks/exhaustive-deps
  return <img src={url || "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg'/>"} alt={alt} style={style} />;
}

async function openAttachmentInTab(authFetch, id) {
  try {
    const r = await authFetch(`/api/attachments/${id}`);
    if (!r || !r.ok) return;
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch {}
}

// Web Share API for any attachment. Falls back to a forced download when
// the browser can't share files (most desktop browsers; older mobile).
// iOS Safari + Android Chrome both support file sharing as of recent
// versions, so the PWA gets the same native share sheet as the iOS app.
async function shareAttachment(authFetch, att) {
  try {
    const r = await authFetch(`/api/attachments/${att.id}`);
    if (!r || !r.ok) return;
    const blob = await r.blob();
    const file = new File([blob], att.filename || "attachment",
      { type: att.mime_type || blob.type || "application/octet-stream" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title: att.filename });
      return;
    }
    // Fallback: trigger a download — gives the user the OS save sheet,
    // which is the closest equivalent on desktop / unsupported browsers.
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = att.filename || "attachment";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    if (e?.name !== "AbortError") console.warn("Share failed:", e);
  }
}

// Lightweight bottom-sheet for non-image attachments — replaces the
// previous "tap → open in new tab" with explicit Open / Share / Delete
// affordances. Modeled on iOS's action sheet so users get the same set
// of choices on both platforms.
function AttachmentActionSheet({ att, onClose, onOpen, onShare, onDelete, canEdit }) {
  if (!att) return null;
  const btn = (label, icon, onClick, danger) => (
    <button onClick={(e) => { e.stopPropagation(); onClick(); onClose(); }}
      style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "14px 18px", width: "100%",
        background: "transparent", border: "none", textAlign: "left",
        fontSize: 15, color: danger ? "var(--red)" : "var(--text, #e8e6f0)",
        cursor: "pointer", fontFamily: "inherit",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
      }}>
      <span style={{ fontSize: 20, width: 24, textAlign: "center" }}>{icon}</span>
      <span style={{ flex: 1 }}>{label}</span>
    </button>
  );
  return (
    <div onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1100,
        background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--card, #1f1838)",
          borderTopLeftRadius: 16, borderTopRightRadius: 16,
          width: "100%", maxWidth: 480,
          paddingBottom: "calc(env(safe-area-inset-bottom) + 8px)",
          boxShadow: "0 -8px 32px rgba(0,0,0,0.4)",
        }}>
        <div style={{
          padding: "12px 18px 8px",
          fontSize: 13, color: "var(--text-muted)", textAlign: "center",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          wordBreak: "break-all",
        }}>{att.filename}</div>
        {btn("Open", "📂", onOpen)}
        {btn("Share", "📤", onShare)}
        {canEdit && btn("Delete", "🗑", onDelete, true)}
        <button onClick={(e) => { e.stopPropagation(); onClose(); }}
          style={{
            display: "block", width: "calc(100% - 24px)", margin: "10px 12px 0",
            padding: "12px", background: "var(--sidebar)",
            border: "none", borderRadius: 10,
            color: "var(--text, #e8e6f0)", fontSize: 14, cursor: "pointer",
            fontFamily: "inherit",
          }}>Cancel</button>
      </div>
    </div>
  );
}

// In-app lightbox for images. Click outside / × / Esc to close. Sized to
// the viewport so the whole photo is visible without scrolling. PDFs and
// other types keep the existing open-in-new-tab behaviour.
// `images` is a list of { id, filename }; `index` is the active one.
// onIndexChange + the on-screen arrows + ←/→ keys cycle through.
function ImageLightbox({ images, index, onIndexChange, onClose, authFetch, onShare, onDelete }) {
  const total = images?.length || 0;
  const cur = total > 0 ? images[index] : null;
  const url = useAuthBlobUrl(authFetch, cur?.id, !!cur);
  const canPrev = index > 0;
  const canNext = index < total - 1;
  const go = (delta) => {
    const next = index + delta;
    if (next < 0 || next >= total) return;
    onIndexChange(next);
  };
  // `go` captures index in the closure; we rebind the effect whenever
  // index/total/onClose change, so the comment-cited deps cover us.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, index, total]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!cur) return null;
  const arrowBtn = (dir, dis, onClick) => (
    <button onClick={(e) => { e.stopPropagation(); onClick(); }}
      disabled={dis}
      aria-label={dir === "prev" ? "Previous photo" : "Next photo"}
      style={{
        position: "absolute", top: "50%", transform: "translateY(-50%)",
        [dir === "prev" ? "left" : "right"]: 16,
        width: 44, height: 44, borderRadius: 22, border: "none",
        background: "rgba(255,255,255,0.12)", color: dis ? "rgba(255,255,255,0.3)" : "#fff",
        fontSize: 26, cursor: dis ? "default" : "pointer",
        display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1,
      }}>{dir === "prev" ? "‹" : "›"}</button>
  );
  return (
    <div onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.92)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "calc(env(safe-area-inset-top) + 16px) 16px calc(env(safe-area-inset-bottom) + 16px)",
      }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{
          position: "absolute", top: "calc(env(safe-area-inset-top) + 12px)", right: 16,
          display: "flex", gap: 8,
        }}>
        {onShare && (
          <button onClick={(e) => { e.stopPropagation(); onShare(cur); }}
            aria-label="Share"
            style={{
              width: 40, height: 40, borderRadius: 20, border: "none",
              background: "rgba(255,255,255,0.12)", color: "#fff", fontSize: 18, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>📤</button>
        )}
        {onDelete && (
          <button onClick={(e) => {
              e.stopPropagation();
              // Confirmation lives in the parent's onDelete handler so we
              // don't double-prompt when the caller already confirms.
              onDelete(cur.id);
              onClose();
            }}
            aria-label="Delete"
            style={{
              width: 40, height: 40, borderRadius: 20, border: "none",
              background: "rgba(255,82,82,0.25)", color: "#fff", fontSize: 18, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>🗑</button>
        )}
        <button onClick={(e) => { e.stopPropagation(); onClose(); }}
          aria-label="Close"
          style={{
            width: 40, height: 40, borderRadius: 20, border: "none",
            background: "rgba(255,255,255,0.12)", color: "#fff", fontSize: 22, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>×</button>
      </div>
      {total > 1 && (
        <div style={{
          position: "absolute", top: "calc(env(safe-area-inset-top) + 18px)", left: 16,
          padding: "4px 10px", borderRadius: 12,
          background: "rgba(255,255,255,0.12)", color: "#fff", fontSize: 12, fontFamily: "var(--ff-mono)",
        }}>{index + 1} / {total}</div>
      )}
      {total > 1 && arrowBtn("prev", !canPrev, () => go(-1))}
      {total > 1 && arrowBtn("next", !canNext, () => go(1))}
      <img src={url || "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg'/>"} alt={cur.filename || ""} onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 6, boxShadow: "0 10px 40px rgba(0,0,0,0.5)" }} />
      {cur.filename && (
        <div onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute", left: 16, right: 16,
            bottom: "calc(env(safe-area-inset-bottom) + 12px)",
            color: "rgba(255,255,255,0.85)", fontSize: 12, textAlign: "center",
            textShadow: "0 1px 2px rgba(0,0,0,0.6)",
          }}>
          {cur.filename}
        </div>
      )}
    </div>
  );
}

// Fetches the image bytes via authFetch (since the endpoint requires a
// JWT) and hands back a blob: URL that the lightbox <img> can render.
function useAuthBlobUrl(authFetch, id, enabled) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!enabled || !id) { setUrl(null); return; }
    let cancelled = false; let created = null;
    authFetch(`/api/attachments/${id}`).then(r => (r && r.ok) ? r.blob() : null).then(b => {
      if (cancelled || !b) return;
      created = URL.createObjectURL(b);
      setUrl(created);
    }).catch(() => {});
    return () => { cancelled = true; if (created) URL.revokeObjectURL(created); };
  }, [authFetch, id, enabled]);
  return url;
}

// Dispatcher: images use the lightbox, anything else falls through to the
// existing open-in-tab path (PDFs etc.). The lightbox needs the full image
// list (filtered out of `items`) plus the index of the clicked one, so
// prev/next can cycle through siblings.
function openAttachment(att, items, authFetch, setLightbox) {
  if (att.mime_type && att.mime_type.startsWith("image/")) {
    const images = (items || []).filter(i => i.mime_type && i.mime_type.startsWith("image/"))
      .map(i => ({ id: i.id, filename: i.filename }));
    const idx = images.findIndex(i => i.id === att.id);
    setLightbox({ images, index: idx >= 0 ? idx : 0 });
    return;
  }
  openAttachmentInTab(authFetch, att.id);
}

// ══════════════════════════════════════════════════════════════════════════
// SegmentAttachments — boarding passes, vouchers, visa PDFs, photos
// Lives inside the expanded segment detail. Upload → SQLite BLOB so the
// daily backup picks them up. 10 MB cap enforced server-side.
// ══════════════════════════════════════════════════════════════════════════
export function SegmentAttachments({ segmentId, tripId, canEdit, authFetch }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [lightbox, setLightbox] = useState(null); // { images, index } or null
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await authFetch(`/api/trips/${tripId}/attachments`);
      if (!r || !r.ok) return;
      const d = await r.json();
      setItems((d.attachments || []).filter(a => a.segment_id === segmentId));
    } catch {}
  }, [authFetch, tripId, segmentId]);
  useEffect(() => { load(); }, [load]);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setBusy(true); setErr(null);
    const failures = [];
    try {
      // Upload sequentially so a bad file doesn't kill the rest. Each
      // request gets its own FormData — the server's /attachments
      // endpoint accepts one file per call.
      for (const file of files) {
        if (file.size > 10 * 1024 * 1024) { failures.push(`${file.name}: too large (>10 MB)`); continue; }
        const fd = new FormData();
        fd.append("file", file);
        const r = await authFetch(`/api/segments/${segmentId}/attachments`, { method: "POST", body: fd });
        if (!r || !r.ok) {
          const j = r ? await r.json().catch(() => ({})) : {};
          failures.push(`${file.name}: ${j.error || "upload failed"}`);
        }
      }
      await load();
      if (failures.length > 0) setErr(failures.join("; "));
    } catch (e) { setErr(e.message); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this attachment?")) return;
    try {
      const r = await authFetch(`/api/attachments/${id}`, { method: "DELETE" });
      if (!r || !r.ok) {
        const j = r ? await r.json().catch(() => ({})) : {};
        setErr(j.error || "Delete failed");
        return;
      }
      setItems(prev => prev.filter(a => a.id !== id));
    } catch (e) { setErr(e.message || "Delete failed"); }
  };

  const iconFor = (mime) => {
    if (!mime) return "📎";
    if (mime.startsWith("image/")) return "🖼";
    if (mime === "application/pdf") return "📄";
    return "📎";
  };
  const fmtSize = (b) => b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${Math.round(b / 1024)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`;

  // Split into images (thumb strip) + docs (filename list). Photos
  // taken on a walk or at an event live with their segment instead of
  // being dumped into the day-level strip; docs (PDFs, vouchers, etc)
  // keep the document-list affordance because they're meant to be
  // tapped open, not browsed visually.
  const { images, docs } = partitionAttachments(items);

  return (
    <div style={{ marginTop: 10 }}>
      {images.length > 0 && (
        <>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--blue, #448aff)", marginBottom: 6 }}>
            Photos <span style={{ color: "var(--text-muted)" }}>({images.length})</span>
          </div>
          <div style={{
            display: "flex", gap: 6, flexWrap: "nowrap",
            overflowX: "auto", overflowY: "hidden",
            WebkitOverflowScrolling: "touch",
            marginBottom: 10, paddingBottom: 2,
          }}>
            {images.map(a => (
              <a key={a.id} href={`/api/attachments/${a.id}`}
                 onClick={e => { e.preventDefault(); e.stopPropagation(); openAttachment(a, images, authFetch, setLightbox); }}
                 title={a.filename}
                 style={{ flex: "0 0 auto", borderRadius: 6, cursor: "pointer" }}>
                <AuthImage src={`/api/attachments/${a.id}`} authFetch={authFetch} alt={a.filename}
                  style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 6, display: "block" }} />
              </a>
            ))}
          </div>
        </>
      )}
      {docs.length > 0 && (
        <>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--blue, #448aff)", marginBottom: 6 }}>
            Documents <span style={{ color: "var(--text-muted)" }}>({docs.length})</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 8 }}>
            {docs.map(a => (
              <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: "var(--sidebar)", borderRadius: 6, fontSize: 12 }}>
                <span style={{ fontSize: 14 }}>{iconFor(a.mime_type)}</span>
                <a href={`/api/attachments/${a.id}`} onClick={e => { e.preventDefault(); e.stopPropagation(); openAttachment(a, docs, authFetch, setLightbox); }} style={{ flex: 1, color: "#e8e6f0", textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer" }}>
                  {a.filename}
                </a>
                <span style={{ fontSize: 10, color: "var(--text-muted)", fontFamily: "var(--ff-mono)" }}>{fmtSize(a.size_bytes)}</span>
                {canEdit && (
                  <button onClick={(e) => { e.stopPropagation(); onDelete(a.id); }}
                    style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 14, padding: 0 }}
                    title="Delete">×</button>
                )}
              </div>
            ))}
          </div>
        </>
      )}
      {items.length === 0 && (
        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--blue, #448aff)", marginBottom: 6 }}>
          Photos &amp; documents
        </div>
      )}
      {canEdit && (
        <div onClick={(e) => e.stopPropagation()}>
          <input ref={fileRef} type="file" accept="application/pdf,image/*" multiple onChange={onUpload} disabled={busy}
            style={{ fontSize: 12, color: "var(--text-dim)" }} />
          {busy && <span style={{ fontSize: 11, color: "var(--text-muted)", marginLeft: 8 }}>Uploading…</span>}
          {err && <div style={{ fontSize: 11, color: "var(--red)", marginTop: 4 }}>{err}</div>}
        </div>
      )}
      {lightbox && (
        <ImageLightbox images={lightbox.images} index={lightbox.index}
          onIndexChange={(i) => setLightbox(l => l ? { ...l, index: i } : l)}
          onClose={() => setLightbox(null)} authFetch={authFetch} />
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════
// DayPhotos — photo + PDF attachments pinned to a specific timeline day.
// Mount one per day. Backs onto the trip-level attachments endpoint with
// day_date set, so they're separate from segment attachments and from the
// existing single-line day_note.
// ══════════════════════════════════════════════════════════════════════════
export function DayPhotos({ tripId, dayDate, canEdit, authFetch }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [lightbox, setLightbox] = useState(null); // { images, index } or null
  const [actionSheet, setActionSheet] = useState(null); // attachment for non-image action sheet
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await authFetch(`/api/trips/${tripId}/attachments`);
      if (!r || !r.ok) return;
      const d = await r.json();
      setItems((d.attachments || []).filter(a => a.day_date === dayDate));
    } catch {}
  }, [authFetch, tripId, dayDate]);
  useEffect(() => { load(); }, [load]);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setBusy(true); setErr(null);
    const failures = [];
    try {
      for (const file of files) {
        if (file.size > 10 * 1024 * 1024) { failures.push(`${file.name}: too large (>10 MB)`); continue; }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("day_date", dayDate);
        const r = await authFetch(`/api/trips/${tripId}/attachments`, { method: "POST", body: fd });
        if (!r || !r.ok) {
          const j = r ? await r.json().catch(() => ({})) : {};
          failures.push(`${file.name}: ${j.error || "upload failed"}`);
        }
      }
      await load();
      if (failures.length > 0) setErr(failures.join("; "));
    } catch (e) { setErr(e.message); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this photo/document?")) return;
    try {
      const r = await authFetch(`/api/attachments/${id}`, { method: "DELETE" });
      if (!r || !r.ok) {
        const j = r ? await r.json().catch(() => ({})) : {};
        setErr(j.error || "Delete failed");
        return;
      }
      setItems(prev => prev.filter(a => a.id !== id));
    } catch (e) { setErr(e.message || "Delete failed"); }
  };

  if (items.length === 0 && !canEdit) return null;
  return (
    <div style={{ marginTop: 6, padding: "6px 12px" }}>
      {items.length > 0 && (
        // Horizontal scrolling strip — matches the iOS app's ScrollView(.horizontal).
        // Was flex-wrap:wrap which on narrow PWA viewports stacked items into a
        // single column once any one item exceeded ~half the screen width.
        <div style={{
          display: "flex", gap: 6, flexWrap: "nowrap",
          overflowX: "auto", overflowY: "hidden",
          WebkitOverflowScrolling: "touch",
          marginBottom: canEdit ? 6 : 0,
          paddingBottom: 2, // breathing room under the scrollbar on touch devices
        }}>
          {items.map(a => {
            const isImage = a.mime_type && a.mime_type.startsWith("image/");
            // iOS parity: images show the thumb only — no caption, no
            // overlay button. Tap opens the lightbox where share + delete
            // live in the toolbar. Non-images get a pill (📄 + name) and
            // tap opens an action sheet with Open / Share / Delete.
            return (
              <a key={a.id} href={`/api/attachments/${a.id}`}
                 onClick={e => {
                   e.preventDefault(); e.stopPropagation();
                   if (isImage) openAttachment(a, items, authFetch, setLightbox);
                   else setActionSheet(a);
                 }}
                 title={a.filename}
                 style={{
                   display: "flex", alignItems: "center", gap: 6,
                   padding: isImage ? 0 : "4px 8px",
                   background: isImage ? "transparent" : "var(--sidebar)",
                   borderRadius: 6, textDecoration: "none", color: "#e8e6f0", fontSize: 11,
                   border: isImage ? "none" : "1px solid var(--border)",
                   cursor: "pointer",
                   flex: "0 0 auto",
                 }}>
                {isImage
                  ? <AuthImage src={`/api/attachments/${a.id}`} authFetch={authFetch} alt={a.filename} style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 6 }} />
                  : <>
                      <span style={{ fontSize: 18 }}>📄</span>
                      <span style={{ maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.filename}</span>
                    </>
                }
              </a>
            );
          })}
        </div>
      )}
      {canEdit && (
        <label onClick={e => e.stopPropagation()} style={{
          display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px",
          background: "var(--sidebar)", border: "1px dashed var(--border)", borderRadius: 6,
          fontSize: 11, color: "var(--text-muted)", cursor: busy ? "wait" : "pointer", fontFamily: "var(--ff-mono)",
        }}>
          {busy ? "uploading…" : "📷 Add photo / doc"}
          <input ref={fileRef} type="file" accept="application/pdf,image/*" multiple onChange={onUpload} disabled={busy} style={{ display: "none" }} />
        </label>
      )}
      {err && <div style={{ fontSize: 11, color: "var(--red)", marginTop: 4 }}>{err}</div>}
      {lightbox && (
        <ImageLightbox images={lightbox.images} index={lightbox.index}
          onIndexChange={(i) => setLightbox(l => l ? { ...l, index: i } : l)}
          onClose={() => setLightbox(null)} authFetch={authFetch}
          onShare={async (cur) => {
            // lightbox `images` carry only { id, filename }, so look up the
            // full attachment (with mime_type) before sharing.
            const full = items.find(a => a.id === cur.id) || cur;
            await shareAttachment(authFetch, full);
          }}
          onDelete={canEdit ? (id) => onDelete(id) : null}
        />
      )}
      {actionSheet && (
        <AttachmentActionSheet att={actionSheet}
          canEdit={canEdit}
          onClose={() => setActionSheet(null)}
          onOpen={() => openAttachmentInTab(authFetch, actionSheet.id)}
          onShare={() => shareAttachment(authFetch, actionSheet)}
          onDelete={() => onDelete(actionSheet.id)}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// WEATHER STRIP — 14-day forecast at the next upcoming destination
// ══════════════════════════════════════════════════════════════════════════
export function WeatherStripPanel({ tripData, timeline, authFetch }) {
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const upcomingHotel = (tripData?.segments || []).find(s => {
    if (s.type !== "hotel" || !s.start_datetime) return false;
    return new Date(s.start_datetime).getTime() >= Date.now() - 86400000;
  });
  const location = upcomingHotel?.start_location || upcomingHotel?.title || tripData?.destination;

  useEffect(() => {
    if (!location) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    const today = new Date();
    const start = today.toISOString().slice(0, 10);
    const end = new Date(today.getTime() + 13 * 86400000).toISOString().slice(0, 10);
    authFetch(`/api/weather/range?location=${encodeURIComponent(location)}&start=${start}&end=${end}`)
      .then(r => r && r.ok ? r.json() : { days: [] })
      .then(d => { if (!cancelled) { setForecast(d); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [location, authFetch]);

  if (loading || !forecast || !forecast.days || forecast.days.length === 0) return null;

  const visibleDays = expanded ? forecast.days : forecast.days.slice(0, 7);
  return (
    <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, padding: 16, marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div>
          <span style={{ fontSize: 14, fontWeight: 600, fontFamily: "Outfit", color: "#e8e6f0" }}>🌤 Weather forecast</span>
          <span style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "var(--ff-mono)", marginLeft: 10 }}>{forecast.location}</span>
        </div>
        {forecast.days.length > 7 && (
          <button onClick={() => setExpanded(e => !e)} style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 12, fontFamily: "var(--ff-mono)" }}>
            {expanded ? "show less" : `+${forecast.days.length - 7} more`}
          </button>
        )}
      </div>
      <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 6 }}>
        {visibleDays.map(d => (
          <div key={d.date} style={{ flex: "0 0 auto", textAlign: "center", padding: "10px 12px", background: "var(--sidebar)", borderRadius: 8, minWidth: 70 }}>
            <div style={{ fontSize: 10, color: "var(--text-muted)", fontFamily: "var(--ff-mono)" }}>
              {new Date(d.date + "T12:00:00").toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" })}
            </div>
            <div style={{ fontSize: 22, margin: "4px 0" }}>{d.icon}</div>
            <div style={{ fontSize: 12, color: "#fff", fontFamily: "var(--ff-mono)" }}>
              <span style={{ color: "var(--amber)" }}>{d.high != null ? `${d.high}°` : "—"}</span>
              <span style={{ color: "var(--text-muted)", margin: "0 3px" }}>/</span>
              <span style={{ color: "var(--blue, #448aff)" }}>{d.low != null ? `${d.low}°` : "—"}</span>
            </div>
            {d.rain_chance > 30 && (
              <div style={{ fontSize: 9, color: "var(--blue, #448aff)", marginTop: 2 }}>💧 {d.rain_chance}%</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// COUNTRY REFERENCE CARDS — plug type, emergency #, tipping, tap water, etc.
// ══════════════════════════════════════════════════════════════════════════
export function CountryCardsPanel({ tripData, authFetch }) {
  const [countries, setCountries] = useState([]);
  const [expanded, setExpanded] = useState(false);

  const codes = (() => {
    try { return JSON.parse(tripData?.countries || "[]"); } catch { return []; }
  })();
  const codesKey = codes.join(",");

  // codes is a fresh array each render; codesKey is the stable identity.
  // authFetch is unstable across renders by design.
  useEffect(() => {
    if (codes.length === 0) return;
    let cancelled = false;
    (async () => {
      const out = [];
      for (const code of codes) {
        try {
          const r = await authFetch(`/api/country/${code}`);
          if (r && r.ok) out.push({ code, ...(await r.json()) });
          else out.push({ code, name: code, _missing: true });
        } catch { out.push({ code, name: code, _missing: true }); }
      }
      if (!cancelled) setCountries(out);
    })();
    return () => { cancelled = true; };
  }, [codesKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (countries.length === 0) return null;

  return (
    <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, padding: 16, marginBottom: 16 }}>
      <div onClick={() => setExpanded(e => !e)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
        <span style={{ fontSize: 14, fontWeight: 600, fontFamily: "Outfit", color: "#e8e6f0" }}>
          🌍 Country quick-reference
          <span style={{ marginLeft: 10, fontSize: 11, color: "var(--text-muted)", fontFamily: "var(--ff-mono)" }}>
            {countries.length} {countries.length === 1 ? "country" : "countries"}
          </span>
        </span>
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{expanded ? "▾" : "▸"}</span>
      </div>
      {expanded && (
        <div style={{ marginTop: 12, display: "grid", gap: 8, gridTemplateColumns: "1fr" }}>
          {countries.map(c => (
            <div key={c.code} style={{ background: "var(--sidebar)", borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#fff", marginBottom: 6 }}>
                {c.name}{c.currency ? <span style={{ color: "var(--text-muted)", fontWeight: 400, fontSize: 12, marginLeft: 6 }}>· {c.currency}</span> : null}
              </div>
              {c._missing ? (
                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>No reference data — please verify locally.</div>
              ) : (
                <div style={{ display: "grid", gap: 4, fontSize: 12, color: "var(--text-dim)", gridTemplateColumns: "auto 1fr", columnGap: 12, rowGap: 4 }}>
                  {c.plug_types && <><span style={{ color: "var(--text-muted)" }}>🔌 Plug</span><span>{c.plug_types.join(", ")}{c.voltage ? ` · ${c.voltage}` : ""}</span></>}
                  {c.emergency && <><span style={{ color: "var(--text-muted)" }}>🚨 Emergency</span><span style={{ fontFamily: "var(--ff-mono)" }}>{c.emergency}</span></>}
                  {c.drive_side && <><span style={{ color: "var(--text-muted)" }}>🚗 Drives on</span><span>{c.drive_side}</span></>}
                  {c.tap_water && <><span style={{ color: "var(--text-muted)" }}>💧 Tap water</span><span>{c.tap_water}</span></>}
                  {c.tipping && <><span style={{ color: "var(--text-muted)" }}>💵 Tipping</span><span>{c.tipping}</span></>}
                  {c.language && <><span style={{ color: "var(--text-muted)" }}>🗣 Language</span><span>{c.language}</span></>}
                  {c.phrases && (
                    <>
                      <span style={{ color: "var(--text-muted)" }}>💬 Phrases</span>
                      <span>
                        {Object.entries(c.phrases).filter(([,v]) => v && v !== "—").map(([k, v]) => <span key={k}><strong>{k}:</strong> {v}{" "}</span>)}
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
          <div style={{ fontSize: 10, color: "var(--text-muted)", fontStyle: "italic", marginTop: 4 }}>
            Quick reference only. Verify locally — embassies and travel advisories are authoritative.
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// NotificationsCard — push subscription toggle, lives at the top of Profile
// ══════════════════════════════════════════════════════════════════════════
export function NotificationsCard({ authFetch }) {
  const [pushState, setPushState] = useState("unknown");
  const [busy, setBusy] = useState(false);
  const [prefs, setPrefs] = useState(null);   // { bookings: true, … } once loaded
  const [groups, setGroups] = useState(null); // { bookings: "Booking status", … } from server
  const [savingPrefs, setSavingPrefs] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) { if (!cancelled) setPushState("unsupported"); return; }
      if (Notification.permission === "denied") { if (!cancelled) setPushState("denied"); return; }
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (!cancelled) setPushState(sub ? "on" : "off");
      } catch { if (!cancelled) setPushState("off"); }
    })();
    return () => { cancelled = true; };
  }, []);
  // Always load prefs so the user can configure them even if push isn't
  // turned on yet — saves them having to click through twice.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await authFetch("/api/notifications/prefs");
        if (r && r.ok) {
          const d = await r.json();
          if (!cancelled) { setPrefs(d.prefs || {}); setGroups(d.groups || {}); }
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [authFetch]);
  const togglePref = async (key) => {
    if (!prefs) return;
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next); setSavingPrefs(true);
    try {
      await authFetch("/api/notifications/prefs", {
        method: "PUT",
        body: JSON.stringify({ prefs: next }),
      });
    } catch {}
    setSavingPrefs(false);
  };
  const enable = async () => {
    setBusy(true);
    const res = await enablePushNotifications(authFetch);
    setBusy(false);
    if (res.ok) setPushState("on");
    else if (res.reason === "denied") setPushState("denied");
    else if (res.reason === "unsupported") setPushState("unsupported");
  };
  const disable = async () => {
    setBusy(true);
    await disablePushNotifications(authFetch);
    setBusy(false);
    setPushState("off");
  };
  return (
    <div className="card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h3 style={{ margin: 0, color: "var(--green)" }}>🔔 Push notifications</h3>
          <div style={{ fontSize: 12, color: "var(--text-dim)", marginTop: 4 }}>
            Smart Alerts (cancellations, gate changes, check-in opens, delays) push to this device when something needs your attention.
          </div>
        </div>
        <div style={{ flexShrink: 0, marginLeft: 12 }}>
          {pushState === "unsupported" && <span style={{ color: "var(--text-muted)", fontSize: 12 }}>Not supported on this device</span>}
          {pushState === "denied" && <span style={{ color: "var(--amber)", fontSize: 12 }}>Blocked — re-enable in browser settings</span>}
          {pushState === "off" && <button className="btn btn-primary btn-sm" onClick={enable} disabled={busy}>{busy ? "…" : "Enable"}</button>}
          {pushState === "on" && <button className="btn btn-secondary btn-sm" onClick={disable} disabled={busy}>{busy ? "…" : "Disable"}</button>}
        </div>
      </div>
      {prefs && groups && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--border)" }}>
          <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 8 }}>
            What to notify me about {savingPrefs && <span style={{ color: "var(--text-dim)", textTransform: "none" }}> · saving…</span>}
          </div>
          {Object.keys(groups).map(key => (
            <label key={key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", cursor: "pointer", fontSize: 13 }}>
              <input
                type="checkbox"
                checked={prefs[key] !== false}
                onChange={() => togglePref(key)}
                style={{ width: 18, height: 18 }}
              />
              <span>{groups[key]}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// BackupCard — daily SQLite backup status + manual trigger
// ══════════════════════════════════════════════════════════════════════════
export function BackupCard({ authFetch }) {
  const [backups, setBackups] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const r = await authFetch("/api/admin/backups");
      if (r && r.ok) { const d = await r.json(); setBackups(d.backups || []); }
    } catch {}
  }, [authFetch]);
  useEffect(() => { load(); }, [load]);
  const triggerNow = async () => {
    setBusy(true);
    try { await authFetch("/api/admin/backup-now", { method: "POST" }); await load(); } catch {}
    setBusy(false);
  };
  const latest = backups && backups[0];
  return (
    <div className="card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ margin: 0, color: "var(--purple)" }}>💾 Database backup</h3>
          <div style={{ fontSize: 12, color: "var(--text-dim)", marginTop: 4 }}>
            Daily snapshot of trips, segments, profile and encrypted PINs to <code>/data/backups</code>. Last 30 days kept.
          </div>
          {backups && (
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6, fontFamily: "var(--ff-mono)" }}>
              {latest ? `Latest: ${latest.name} · ${latest.size_kb} KB · ${new Date(latest.modified).toLocaleString("en-AU")}` : "No backups yet — click Run now."}
              {backups.length > 1 && <div>{backups.length} snapshots on disk.</div>}
            </div>
          )}
        </div>
        <div style={{ flexShrink: 0, marginLeft: 12 }}>
          <button className="btn btn-secondary btn-sm" onClick={triggerNow} disabled={busy}>{busy ? "…" : "Run now"}</button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// AllAttachmentsPanel — every file across every trip the user has access to.
// Lives in ProfileView. Uses /api/attachments (server joins to trip metadata
// so we don't have to fan out per-trip calls).
// ══════════════════════════════════════════════════════════════════════════
export function AllAttachmentsPanel({ authFetch }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [lightbox, setLightbox] = useState(null); // {images, index} or null

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await authFetch("/api/attachments");
      if (!r || !r.ok) { setErr("Failed to load"); setLoading(false); return; }
      const d = await r.json();
      setItems(d.attachments || []);
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  }, [authFetch]);
  useEffect(() => { load(); }, [load]);

  // Group by trip, preserving the server's created_at-DESC order.
  const groups = (() => {
    const seen = new Map();
    const out = [];
    for (const a of items) {
      if (seen.has(a.trip_id)) {
        out[seen.get(a.trip_id)].items.push(a);
      } else {
        seen.set(a.trip_id, out.length);
        out.push({
          trip_id: a.trip_id,
          title: a.trip_title || `Trip ${a.trip_id}`,
          dates: a.trip_start && a.trip_end ? `${a.trip_start} → ${a.trip_end}` : (a.trip_start || a.trip_end || null),
          items: [a],
        });
      }
    }
    return out;
  })();

  const fileIcon = (mime) => {
    if (mime && mime.startsWith("image/")) return "🖼";
    if (mime === "application/pdf") return "📄";
    return "📎";
  };

  return (
    <div className="card">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
           onClick={() => setExpanded(v => !v)}>
        <div>
          <h3 style={{ margin: 0, color: "var(--green)" }}>📎 All attachments</h3>
          <div style={{ fontSize: 12, color: "var(--text-dim)", marginTop: 4 }}>
            Every boarding pass, voucher and photo across every trip you can see — {items.length} file{items.length === 1 ? "" : "s"} in total.
          </div>
        </div>
        <span style={{ fontSize: 14, color: "var(--text-muted)" }}>{expanded ? "▾" : "▸"}</span>
      </div>
      {expanded && (
        <div style={{ marginTop: 12 }}>
          {loading && <div style={{ fontSize: 12, color: "var(--text-dim)" }}>Loading…</div>}
          {err && <div style={{ fontSize: 12, color: "var(--red)" }}>{err}</div>}
          {!loading && !err && groups.length === 0 && (
            <div style={{ fontSize: 12, color: "var(--text-dim)" }}>
              Nothing uploaded yet. Add files from any trip's Attachments panel.
            </div>
          )}
          {groups.map(g => (
            <div key={g.trip_id} style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--border)" }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{g.title}</div>
              {g.dates && <div style={{ fontSize: 11, color: "var(--text-dim)", marginBottom: 6 }}>{g.dates}</div>}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {g.items.map(a => (
                  <div key={a.id}
                       onClick={() => openAttachment(a, g.items, authFetch, setLightbox)}
                       style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px",
                                background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6,
                                fontSize: 12, cursor: "pointer", maxWidth: 240, minWidth: 0 }}>
                    <span>{fileIcon(a.mime_type)}</span>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.filename}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {lightbox && (
        <ImageLightbox
          images={lightbox.images}
          index={lightbox.index}
          onIndexChange={(i) => setLightbox(l => ({ ...l, index: i }))}
          onClose={() => setLightbox(null)}
          authFetch={authFetch}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// PinnedPlacesPanel — cross-trip favourites. Pinned via the 📌 button on
// Near-my-hotel sections; the server surfaces matches within 5 km of the
// anchor on future trips. This panel is just the management surface for
// removing stale entries. Lives in ProfileView next to AllAttachmentsPanel.
// ══════════════════════════════════════════════════════════════════════════
export function PinnedPlacesPanel({ authFetch }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await authFetch("/api/profile/pinned");
      if (!r || !r.ok) { setErr("Failed to load"); setLoading(false); return; }
      const d = await r.json();
      setItems(d.pinned || []);
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  }, [authFetch]);
  useEffect(() => { load(); }, [load]);

  const remove = async (p) => {
    if (!window.confirm(`Unpin “${p.name}”?`)) return;
    try {
      await authFetch(`/api/profile/pinned/${p.id}`, { method: "DELETE" });
      setItems((prev) => prev.filter((x) => x.id !== p.id));
    } catch {}
  };

  return (
    <div className="card">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
           onClick={() => setExpanded(v => !v)}>
        <div>
          <h3 style={{ margin: 0, color: "var(--amber)" }}>📌 Pinned places</h3>
          <div style={{ fontSize: 12, color: "var(--text-dim)", marginTop: 4 }}>
            Cross-trip favourites surfaced near future hotels — {items.length} pin{items.length === 1 ? "" : "s"}.
          </div>
        </div>
        <span style={{ fontSize: 14, color: "var(--text-muted)" }}>{expanded ? "▾" : "▸"}</span>
      </div>
      {expanded && (
        <div style={{ marginTop: 12 }}>
          {loading && <div style={{ fontSize: 12, color: "var(--text-dim)" }}>Loading…</div>}
          {err && <div style={{ fontSize: 12, color: "var(--red)" }}>{err}</div>}
          {!loading && !err && items.length === 0 && (
            <div style={{ fontSize: 12, color: "var(--text-dim)" }}>
              Nothing pinned yet. Tap the 📌 next to a venue on Near-my-hotel.
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {items.map((p) => {
              const mapsUrl = (p.lat && p.lng)
                ? `https://www.google.com/maps/?q=${p.lat},${p.lng}+(${encodeURIComponent(p.name)})`
                : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(p.name)}`;
              return (
                <div key={p.id} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 10px",
                                          background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6 }}>
                  <span style={{ flexShrink: 0, marginTop: 2 }}>📌</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{p.name}</div>
                    {p.address && <div style={{ fontSize: 11, color: "var(--text-dim)" }}>{p.address}</div>}
                    {p.notes && <div style={{ fontSize: 11, color: "var(--text-dim)", fontStyle: "italic" }}>{p.notes}</div>}
                  </div>
                  <a href={mapsUrl} target="_blank" rel="noreferrer noopener"
                     style={{ color: "var(--blue)", fontSize: 16, textDecoration: "none", padding: "0 4px" }}
                     title="Open in Google Maps">📍</a>
                  <button onClick={() => remove(p)}
                          title="Unpin"
                          style={{ background: "none", border: "none", color: "var(--red)", opacity: 0.7,
                                   cursor: "pointer", fontSize: 14, padding: "0 4px" }}>
                    🗑
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// WalkingToursPanel — user's library of ordered-stop walking tours.
//
// Thin wrapper over the server:
//   GET  /api/tours                            → list summaries
//   GET  /api/tours/:id                        → full tour with parts+stops
//   POST /api/tours                            → create
//   PUT  /api/tours/:id                        → replace
//   DELETE /api/tours/:id                      → cascade delete
//   GET  /api/tours/:id/export?target=amap|google|apple|text
//
// Mainland PRC behaviour (option c per plan): no embedded map in the
// PWA — OSM tiles are blocked by the GFW inside mainland and we
// don't want to pay for Amap raster tiles yet. We rely on the deep-
// link affordance, which is the behaviour you'd want anyway (the
// native app knows the user's location, routing, saved favourites).
// ══════════════════════════════════════════════════════════════════════════
export function WalkingToursPanel({ authFetch }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [openTourId, setOpenTourId] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | tourId

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await authFetch("/api/tours");
      if (!r || !r.ok) { setErr("Failed to load"); setLoading(false); return; }
      const d = await r.json();
      setItems(d.tours || []);
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  }, [authFetch]);
  useEffect(() => { load(); }, [load]);

  const removeTour = async (t) => {
    if (!window.confirm(`Delete tour “${t.title}”?`)) return;
    try {
      await authFetch(`/api/tours/${t.id}`, { method: "DELETE" });
      setItems((prev) => prev.filter((x) => x.id !== t.id));
      if (openTourId === t.id) setOpenTourId(null);
    } catch {}
  };

  const onSaved = async () => { setEditing(null); await load(); };

  return (
    <div className="card">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
           onClick={() => setExpanded(v => !v)}>
        <div>
          <h3 style={{ margin: 0, color: "var(--green)" }}>🚶 Walking tours</h3>
          <div style={{ fontSize: 12, color: "var(--text-dim)", marginTop: 4 }}>
            Ordered stop lists — open in Amap, Google or Apple Maps · {items.length}
          </div>
        </div>
        <span style={{ fontSize: 14, color: "var(--text-muted)" }}>{expanded ? "▾" : "▸"}</span>
      </div>
      {expanded && (
        <div style={{ marginTop: 12 }}>
          <button
            onClick={(e) => { e.stopPropagation(); setEditing("new"); }}
            style={{
              background: "var(--green)", color: "#0b0820", border: "none",
              padding: "6px 12px", borderRadius: 6, fontWeight: 600, cursor: "pointer", fontSize: 13,
            }}>+ New tour</button>

          {loading && <div style={{ fontSize: 12, color: "var(--text-dim)", marginTop: 10 }}>Loading…</div>}
          {err && <div style={{ fontSize: 12, color: "var(--red)", marginTop: 10 }}>{err}</div>}
          {!loading && !err && items.length === 0 && (
            <div style={{ fontSize: 12, color: "var(--text-dim)", marginTop: 10 }}>
              No tours yet. Create one and it'll surface here on any device.
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
            {items.map((t) => (
              <TourCard
                key={t.id}
                tour={t}
                open={openTourId === t.id}
                onToggle={() => setOpenTourId(openTourId === t.id ? null : t.id)}
                onEdit={() => setEditing(t.id)}
                onDelete={() => removeTour(t)}
                authFetch={authFetch}
              />
            ))}
          </div>

          {editing !== null && (
            <TourEditorDialog
              tourId={editing === "new" ? null : editing}
              onClose={() => setEditing(null)}
              onSaved={onSaved}
              authFetch={authFetch}
            />
          )}
        </div>
      )}
    </div>
  );
}

function TourCard({ tour, open, onToggle, onEdit, onDelete, authFetch }) {
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  useEffect(() => {
    if (!open) return;
    if (detail && detail.id === tour.id) return;
    setLoadingDetail(true);
    authFetch(`/api/tours/${tour.id}`)
      .then((r) => r && r.ok ? r.json() : null)
      .then((d) => d && d.tour && setDetail(d.tour))
      .finally(() => setLoadingDetail(false));
  }, [open, tour.id, authFetch]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 8, background: "var(--bg)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 10, cursor: "pointer" }}
           onClick={onToggle}>
        <span style={{ fontSize: 18 }}>🚶</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{tour.title}</div>
          {tour.notes && <div style={{ fontSize: 11, color: "var(--text-dim)" }}>{tour.notes}</div>}
        </div>
        <button onClick={(e) => { e.stopPropagation(); onEdit(); }}
                style={{ background: "none", border: "1px solid var(--border)", color: "var(--text-dim)",
                         borderRadius: 4, padding: "2px 8px", fontSize: 11, cursor: "pointer" }}>Edit</button>
        <button onClick={(e) => { e.stopPropagation(); onDelete(); }}
                style={{ background: "none", border: "none", color: "var(--red)", opacity: 0.6,
                         cursor: "pointer", fontSize: 14, padding: "0 4px" }}>🗑</button>
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{open ? "▾" : "▸"}</span>
      </div>
      {open && (
        <div style={{ padding: "0 10px 10px 10px" }}>
          {loadingDetail && <div style={{ fontSize: 11, color: "var(--text-dim)" }}>Loading stops…</div>}
          {detail && <TourDetailBody tour={detail} authFetch={authFetch} />}
        </div>
      )}
    </div>
  );
}

function TourDetailBody({ tour, authFetch }) {
  let counter = 0;
  const flatTour = tour.parts.length === 1 && !tour.parts[0].title;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {tour.parts.map((part, pIdx) => (
        <div key={pIdx}>
          {!flatTour && part.title && (
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase",
                          color: "var(--text-dim)", margin: "6px 0 4px" }}>
              {part.title}
            </div>
          )}
          {part.stops.map((stop) => {
            counter += 1;
            return <StopRow key={stop.id} stop={stop} number={counter} />;
          })}
        </div>
      ))}
      <ExportBar tourId={tour.id} authFetch={authFetch} />
    </div>
  );
}

function StopRow({ stop, number }) {
  const name = stop.name_en || stop.name_local || "Stop";
  const local = stop.name_local && stop.name_local !== name ? stop.name_local : null;
  const flags = [];
  if (stop.exterior_only) flags.push({ t: "view from outside only", c: "var(--blue)" });
  if (stop.id_required) flags.push({ t: "bring passport / ID", c: "var(--amber)" });
  if (stop.ticket_required) flags.push({ t: "ticket required", c: "var(--blue)" });
  if (stop.closed_days) flags.push({ t: `closed ${stop.closed_days}`, c: "var(--red)" });
  if (stop.opening_hours) flags.push({ t: stop.opening_hours, c: "var(--purple)" });
  return (
    <div style={{ display: "flex", gap: 8, padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
      <div style={{ color: "var(--green)", fontWeight: 600, fontSize: 12, width: 24 }}>{number}.</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500 }}>
          {name}
          {local && <span style={{ marginLeft: 6, fontSize: 11, color: "var(--text-dim)" }}>{local}</span>}
        </div>
        {stop.arrival_time && (
          <div style={{ fontSize: 11, color: "var(--text-muted)" }}>~ {stop.arrival_time}</div>
        )}
        {flags.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
            {flags.map((f, i) => (
              <span key={i} style={{
                fontSize: 10, fontWeight: 500, padding: "2px 6px", borderRadius: 999,
                color: f.c, background: `${f.c}20`,
              }}>{f.t}</span>
            ))}
          </div>
        )}
        {stop.notes && <div style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 2 }}>{stop.notes}</div>}
      </div>
    </div>
  );
}

function ExportBar({ tourId, authFetch }) {
  const [target, setTarget] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const open = async (nextTarget) => {
    if (target === nextTarget) { setTarget(null); setData(null); return; }
    setTarget(nextTarget); setData(null); setLoading(true);
    try {
      const r = await authFetch(`/api/tours/${tourId}/export?target=${nextTarget}`);
      if (r && r.ok) setData(await r.json());
    } catch {}
    setLoading(false);
  };

  const btnStyle = (t) => ({
    background: target === t ? "var(--green)" : "var(--bg-light)",
    color: target === t ? "#0b0820" : "var(--text)",
    border: "1px solid var(--border)",
    borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: "pointer",
  });

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <button onClick={() => open("amap")} style={btnStyle("amap")}>Amap (高德)</button>
        <button onClick={() => open("google")} style={btnStyle("google")}>Google Maps</button>
        <button onClick={() => open("apple")} style={btnStyle("apple")}>Apple Maps</button>
        <button onClick={() => open("text")} style={btnStyle("text")}>Copy as text</button>
      </div>
      {loading && <div style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 6 }}>Loading…</div>}
      {!loading && data && (
        <div style={{ marginTop: 8, padding: 8, background: "var(--bg-light)", borderRadius: 6 }}>
          {target === "amap" && data.markers && (
            <>
              <ExportLinkList title="Full tour on map (chunked)" links={data.markers} />
              <ExportLinkList title="Per-leg walking directions" links={data.legs} />
            </>
          )}
          {(target === "google" || target === "apple") && data.links && (
            <ExportLinkList title={target === "google" ? "Walking directions" : "Pin per stop"} links={data.links} />
          )}
          {target === "text" && data.text && <TextExport text={data.text} />}
        </div>
      )}
    </div>
  );
}

function ExportLinkList({ title, links }) {
  if (!links || links.length === 0) return null;
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 0.5,
                    textTransform: "uppercase", color: "var(--text-dim)", marginBottom: 4 }}>
        {title}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {links.map((l, i) => (
          <a key={i} href={l.url} target="_blank" rel="noreferrer noopener"
             style={{ fontSize: 12, color: "var(--green)", textDecoration: "none",
                      padding: "4px 8px", background: "var(--bg)", borderRadius: 4,
                      border: "1px solid var(--border)" }}>
            ↗ {l.title}
          </a>
        ))}
      </div>
    </div>
  );
}

function TextExport({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch {}
  };
  return (
    <div>
      <pre style={{ fontSize: 11, whiteSpace: "pre-wrap", color: "var(--text)",
                    background: "var(--bg)", padding: 8, borderRadius: 4, margin: 0,
                    fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>{text}</pre>
      <button onClick={copy} style={{ marginTop: 6, background: "var(--green)", color: "#0b0820",
                                      border: "none", padding: "4px 10px", borderRadius: 4,
                                      fontSize: 12, cursor: "pointer", fontWeight: 600 }}>
        {copied ? "Copied ✓" : "Copy to clipboard"}
      </button>
    </div>
  );
}

function TourEditorDialog({ tourId, onClose, onSaved, authFetch }) {
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [stops, setStops] = useState([blankStop()]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState(null);

  const generate = async () => {
    const p = aiPrompt.trim();
    if (!p) return;
    setAiBusy(true); setAiError(null);
    try {
      const r = await authFetch("/api/tours/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: p }),
      });
      if (!r || !r.ok) {
        const d = r ? await r.json().catch(() => null) : null;
        setAiError(d?.error || "Generation failed");
      } else {
        const d = await r.json();
        const g = d?.tour;
        if (g && Array.isArray(g.parts)) {
          setTitle(g.title || "");
          setNotes(g.notes || "");
          const flat = g.parts.flatMap((part) => part.stops || []);
          setStops(flat.length ? flat.map(stopFromServer) : [blankStop()]);
        } else {
          setAiError("Model returned no tour");
        }
      }
    } catch (e) {
      setAiError(e.message);
    }
    setAiBusy(false);
  };

  useEffect(() => {
    if (!tourId) return;
    authFetch(`/api/tours/${tourId}`)
      .then((r) => r && r.ok ? r.json() : null)
      .then((d) => {
        if (!d || !d.tour) return;
        setTitle(d.tour.title);
        setNotes(d.tour.notes || "");
        const flat = (d.tour.parts || []).flatMap((p) => p.stops);
        setStops(flat.length ? flat.map(stopFromServer) : [blankStop()]);
      });
  }, [tourId, authFetch]);

  const update = (idx, patch) => setStops((prev) =>
    prev.map((s, i) => (i === idx ? { ...s, ...patch } : s))
  );
  const addStop = () => setStops((prev) => [...prev, blankStop()]);
  const removeStop = (idx) => setStops((prev) =>
    prev.length === 1 ? prev : prev.filter((_, i) => i !== idx)
  );

  const save = async () => {
    setErr(null);
    if (!title.trim()) { setErr("Title required"); return; }
    const payloadStops = stops
      .filter((s) => (s.name_en || s.name_local) && !isNaN(parseFloat(s.lat)) && !isNaN(parseFloat(s.lng)))
      .map((s) => ({
        name_en: s.name_en || null,
        name_local: s.name_local || null,
        lat: parseFloat(s.lat), lng: parseFloat(s.lng),
        coord_system: s.coord_system,
        arrival_time: s.arrival_time || null,
        notes: s.notes || null,
        exterior_only: !!s.exterior_only,
        id_required: !!s.id_required,
        ticket_required: !!s.ticket_required,
        closed_days: s.closed_days || null,
        opening_hours: s.opening_hours || null,
      }));
    if (payloadStops.length === 0) { setErr("Add at least one stop with name + coords"); return; }

    setSaving(true);
    try {
      const url = tourId ? `/api/tours/${tourId}` : "/api/tours";
      const method = tourId ? "PUT" : "POST";
      const r = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: title.trim(), mode: "walking", notes: notes || null, stops: payloadStops }),
      });
      if (r && r.ok) {
        onSaved();
      } else {
        const d = r ? await r.json().catch(() => null) : null;
        setErr(d?.error || "Save failed");
      }
    } catch (e) {
      setErr(e.message);
    }
    setSaving(false);
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center",
    }} onClick={onClose}>
      <div style={{
        background: "var(--card)", borderRadius: 8, maxWidth: 640, width: "92%",
        maxHeight: "88vh", overflow: "auto", padding: 16,
      }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 10px 0" }}>{tourId ? "Edit tour" : "New walking tour"}</h3>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ background: "var(--bg-light)", border: "1px solid var(--border)",
                        borderRadius: 6, padding: 10, display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ fontSize: 11, color: "var(--text-dim)" }}>
              ✨ <strong>Generate with AI</strong> — describe the walk (named route, theme, or neighbourhood);
              Claude searches the web and fills the stops. You can tweak before saving.
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <input value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)}
                     placeholder="e.g. Shanghai Bund heritage walk"
                     style={{ ...inputStyle, flex: 1 }} />
              <button onClick={generate} disabled={aiBusy || !aiPrompt.trim()} style={{
                background: "var(--green)", color: "#0b0820", border: "none",
                padding: "4px 12px", borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: "pointer",
                opacity: aiBusy || !aiPrompt.trim() ? 0.5 : 1,
              }}>{aiBusy ? "Generating…" : "Generate"}</button>
            </div>
            {aiError && <div style={{ fontSize: 11, color: "var(--red)" }}>{aiError}</div>}
            {aiBusy && <div style={{ fontSize: 11, color: "var(--text-dim)" }}>Searching + composing — about 20 seconds…</div>}
          </div>
          <input value={title} onChange={(e) => setTitle(e.target.value)}
                 placeholder="Title (e.g. Shanghai Bund walk)"
                 style={inputStyle} />
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                    placeholder="Notes (optional)" rows={2}
                    style={{ ...inputStyle, resize: "vertical" }} />
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 0.5,
                        textTransform: "uppercase", color: "var(--text-dim)", marginTop: 6 }}>Stops</div>
          {stops.map((s, i) => (
            <StopEditor key={i} stop={s} index={i} onUpdate={update}
                        onRemove={stops.length > 1 ? () => removeStop(i) : null} />
          ))}
          <button onClick={addStop} style={{
            background: "var(--bg-light)", border: "1px dashed var(--border)",
            padding: "6px 10px", borderRadius: 4, fontSize: 12, cursor: "pointer",
            color: "var(--green)", fontWeight: 600,
          }}>+ Add stop</button>
          {err && <div style={{ fontSize: 12, color: "var(--red)" }}>{err}</div>}
          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", marginTop: 8 }}>
            <button onClick={onClose} style={{
              background: "none", border: "1px solid var(--border)", color: "var(--text-dim)",
              padding: "6px 14px", borderRadius: 4, cursor: "pointer", fontSize: 12,
            }}>Cancel</button>
            <button onClick={save} disabled={saving} style={{
              background: "var(--green)", color: "#0b0820", border: "none",
              padding: "6px 14px", borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: "pointer",
              opacity: saving ? 0.6 : 1,
            }}>{saving ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StopEditor({ stop, index, onUpdate, onRemove }) {
  const [showAdv, setShowAdv] = useState(false);
  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 6, padding: 8, background: "var(--bg)" }}>
      <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
        <span style={{ color: "var(--green)", fontWeight: 600, fontSize: 12 }}>{index + 1}.</span>
        <input value={stop.name_en} onChange={(e) => onUpdate(index, { name_en: e.target.value })}
               placeholder="Name" style={{ ...inputStyle, flex: 1 }} />
        {onRemove && (
          <button onClick={onRemove} style={{ background: "none", border: "none", color: "var(--red)",
                                              cursor: "pointer", fontSize: 14 }}>✕</button>
        )}
      </div>
      <input value={stop.name_local} onChange={(e) => onUpdate(index, { name_local: e.target.value })}
             placeholder="Local name (e.g. 外滩美术馆)" style={{ ...inputStyle, marginBottom: 4 }} />
      <div style={{ display: "flex", gap: 6 }}>
        <input value={stop.lat} onChange={(e) => onUpdate(index, { lat: e.target.value })}
               placeholder="Lat" inputMode="decimal" style={{ ...inputStyle, flex: 1 }} />
        <input value={stop.lng} onChange={(e) => onUpdate(index, { lng: e.target.value })}
               placeholder="Lng" inputMode="decimal" style={{ ...inputStyle, flex: 1 }} />
        <select value={stop.coord_system} onChange={(e) => onUpdate(index, { coord_system: e.target.value })}
                style={{ ...inputStyle, flex: "0 0 auto" }}>
          <option value="WGS84">WGS84</option>
          <option value="GCJ02">GCJ02</option>
        </select>
      </div>
      <button onClick={() => setShowAdv((v) => !v)} style={{
        background: "none", border: "none", color: "var(--text-dim)", cursor: "pointer",
        fontSize: 11, padding: "4px 0", marginTop: 4,
      }}>{showAdv ? "− Hide practical info" : "+ Practical info / flags"}</button>
      {showAdv && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4 }}>
          <div style={{ display: "flex", gap: 6 }}>
            <input value={stop.arrival_time} onChange={(e) => onUpdate(index, { arrival_time: e.target.value })}
                   placeholder="Arrival HH:MM" style={{ ...inputStyle, flex: 1 }} />
            <input value={stop.closed_days} onChange={(e) => onUpdate(index, { closed_days: e.target.value })}
                   placeholder="Closed (Mon)" style={{ ...inputStyle, flex: 1 }} />
          </div>
          <input value={stop.opening_hours} onChange={(e) => onUpdate(index, { opening_hours: e.target.value })}
                 placeholder="Opening hours (09:00–18:00)" style={inputStyle} />
          <div style={{ display: "flex", gap: 10, fontSize: 11, color: "var(--text-dim)", flexWrap: "wrap" }}>
            <label><input type="checkbox" checked={!!stop.exterior_only}
                           onChange={(e) => onUpdate(index, { exterior_only: e.target.checked })} /> Exterior only</label>
            <label><input type="checkbox" checked={!!stop.id_required}
                           onChange={(e) => onUpdate(index, { id_required: e.target.checked })} /> ID required</label>
            <label><input type="checkbox" checked={!!stop.ticket_required}
                           onChange={(e) => onUpdate(index, { ticket_required: e.target.checked })} /> Ticket required</label>
          </div>
          <input value={stop.notes} onChange={(e) => onUpdate(index, { notes: e.target.value })}
                 placeholder="Notes" style={inputStyle} />
        </div>
      )}
    </div>
  );
}

const inputStyle = {
  background: "var(--bg)", border: "1px solid var(--border)", color: "var(--text)",
  padding: "4px 8px", borderRadius: 4, fontSize: 12, outline: "none",
};

function blankStop() {
  return {
    name_en: "", name_local: "", lat: "", lng: "", coord_system: "WGS84",
    arrival_time: "", notes: "",
    exterior_only: false, id_required: false, ticket_required: false,
    closed_days: "", opening_hours: "",
  };
}

function stopFromServer(s) {
  return {
    name_en: s.name_en || "",
    name_local: s.name_local || "",
    lat: String(s.lat ?? ""),
    lng: String(s.lng ?? ""),
    coord_system: s.coord_system || "WGS84",
    arrival_time: s.arrival_time || "",
    notes: s.notes || "",
    exterior_only: !!s.exterior_only,
    id_required: !!s.id_required,
    ticket_required: !!s.ticket_required,
    closed_days: s.closed_days || "",
    opening_hours: s.opening_hours || "",
  };
}

// ══════════════════════════════════════════════════════════════════════════
// ProfileTagsCard — free-text user preferences (cuisine / dietary / interest)
// that drive the near-hotel-utilities recommender. Lives in ProfileView.
// Server endpoints: GET/POST/DELETE /api/profile/tags.
// ══════════════════════════════════════════════════════════════════════════

// Single flat preferences list — Tim didn't want the bucket split
// when there are only two of them. Each chip still carries its `kind`
// under the hood so server-side cuisine routing keeps working
// (cuisine kind triggers the `restaurant + cuisine~<tag>` fallback
// for unmapped tags like "Catalan"). Free-text adds default to
// kind:"interest" — covers the broad case; can be promoted to cuisine
// later by Claude if needed.
const TAG_SUGGESTIONS = [
  // Cuisines
  { label: "Asian",          kind: "cuisine" },
  { label: "Chinese",        kind: "cuisine" },
  { label: "Japanese",       kind: "cuisine" },
  { label: "Thai",           kind: "cuisine" },
  { label: "Vietnamese",     kind: "cuisine" },
  { label: "Indian",         kind: "cuisine" },
  { label: "Italian",        kind: "cuisine" },
  { label: "Spanish",        kind: "cuisine" },
  { label: "Mexican",        kind: "cuisine" },
  { label: "Mediterranean",  kind: "cuisine" },
  { label: "Sushi",          kind: "cuisine" },
  { label: "Seafood",        kind: "cuisine" },
  // Interests
  { label: "Craft beer",     kind: "interest" },
  { label: "Wine bar",       kind: "interest" },
  { label: "Pub",            kind: "interest" },
  { label: "Cafe",           kind: "interest" },
  { label: "Bakery",         kind: "interest" },
  { label: "Supermarket",    kind: "interest" },
  { label: "Pharmacy",       kind: "interest" },
  { label: "ATM",            kind: "interest" },
  { label: "Train station",  kind: "interest" },
  { label: "Gym",            kind: "interest" },
  { label: "Bookshop",       kind: "interest" },
  { label: "Vinyl",          kind: "interest" },
  { label: "Cycling",        kind: "interest" },
  { label: "Bike hire",      kind: "interest" },
];

export function ProfileTagsCard({ authFetch }) {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch("/api/profile/tags");
      if (r && r.ok) {
        const d = await r.json();
        setTags(d.tags || []);
      }
    } catch {}
    setLoading(false);
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const addTag = async (tagValue, kind) => {
    const value = (tagValue || "").trim();
    if (!value) return;
    setBusy(true);
    try {
      const r = await authFetch("/api/profile/tags", {
        method: "POST",
        body: JSON.stringify({ kind, tag: value }),
      });
      if (r && r.ok) { const d = await r.json(); setTags(d.tags || []); }
    } catch {}
    setBusy(false);
  };

  const remove = async (id) => {
    setBusy(true);
    try {
      const r = await authFetch(`/api/profile/tags/${id}`, { method: "DELETE" });
      if (r && r.ok) { const d = await r.json(); setTags(d.tags || []); }
    } catch {}
    setBusy(false);
  };

  // Brief green flash when a suggestion chip is tapped — confirms the
  // save without needing the user to scan the saved-row above for the
  // newly-arrived chip. Resets after 600 ms.
  const [flashLabel, setFlashLabel] = useState(null);
  const flash = (label) => {
    setFlashLabel(label);
    setTimeout(() => setFlashLabel((cur) => (cur === label ? null : cur)), 600);
  };

  const haveLower = new Set(tags.map((t) => t.tag.toLowerCase()));
  const remainingSuggestions = TAG_SUGGESTIONS.filter(
    (s) => !haveLower.has(s.label.toLowerCase())
  );

  return (
    <div className="card">
      <h3 style={{ margin: 0, color: "var(--green)" }}>✨ My preferences</h3>
      <div style={{ fontSize: 12, color: "var(--text-dim)", marginTop: 4, marginBottom: 12 }}>
        Used by <em>Near my hotel</em> on hotel cards to surface places that match what you like — Asian food, craft beer, supermarkets, pharmacies, whatever you'd want to find nearby.
      </div>
      {loading ? (
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Loading…</div>
      ) : (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
            {tags.map((t) => (
              <span key={t.id} style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "4px 10px", background: "var(--card)",
                border: "1px solid var(--green)", borderRadius: 14, fontSize: 12, color: "var(--text)",
              }}>
                {t.tag}
                <button onClick={() => remove(t.id)} disabled={busy}
                  title="Remove"
                  style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", padding: 0, fontSize: 14, lineHeight: 1 }}>×</button>
              </span>
            ))}
            {tags.length === 0 && (
              <span style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" }}>Nothing yet — tap a chip below or type your own.</span>
            )}
          </div>
          {remainingSuggestions.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
              {remainingSuggestions.map((s) => {
                const flashing = flashLabel === s.label;
                return (
                  <button key={s.label}
                    onClick={() => { flash(s.label); addTag(s.label, s.kind); }}
                    disabled={busy}
                    style={{
                      padding: "3px 9px",
                      background: flashing ? "var(--green)" : "transparent",
                      border: "1px dashed var(--green)", borderRadius: 12,
                      fontSize: 11,
                      color: flashing ? "#111" : "var(--green)",
                      cursor: busy ? "default" : "pointer",
                      transition: "background 0.18s, color 0.18s",
                    }}>
                    {flashing ? "✓" : "+"} {s.label}
                  </button>
                );
              })}
            </div>
          )}
          <div style={{ display: "flex", gap: 6 }}>
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(draft, "interest"); setDraft(""); } }}
              placeholder="Add your own…"
              disabled={busy}
              style={{ flex: 1, padding: "6px 10px", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6, color: "var(--text)", fontSize: 13 }}
            />
            <button className="btn btn-secondary btn-sm" onClick={() => { addTag(draft, "interest"); setDraft(""); }} disabled={busy || !draft.trim()}>Add</button>
          </div>
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// NearMyHotelPanel — fires /api/ai/near-hotel-utilities for a hotel segment
// and renders a section per profile tag with the actual OSM venues sorted
// by walking distance. Pull-to-open from the timeline hotel card.
// ══════════════════════════════════════════════════════════════════════════

export function NearMyHotelPanel({ segmentId, tripId, hotelDate, todayMode = false, authFetch, onClose, onAdded, onOpenAiTab }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  // Expanded-section state — sections collapse by default when the
  // response has >2 of them so the user can scan headers first
  // ("Supermarket · 24 nearby"). Pinned section, when present, stays
  // expanded.
  const [expandedSections, setExpandedSections] = useState(new Set());
  // Pinned + hidden venue keys (lat,lng+name) — local mirror of the
  // server's `pinned_places` + `user_venue_dismissals` so we can
  // optimistically reflect the user's tap without a refetch.
  const [hiddenKeys, setHiddenKeys] = useState(new Set());
  const [pinnedKeys, setPinnedKeys] = useState(new Set());
  const [busyKey, setBusyKey] = useState(null);
  const venueDismissKey = (v) => `${(v.name || v.display_name || "").toLowerCase()}@${(v.lat ?? 0).toFixed ? v.lat.toFixed(4) : v.lat || 0},${(v.lng ?? 0).toFixed ? v.lng.toFixed(4) : v.lng || 0}`;
  const hideVenue = async (v) => {
    const key = venueDismissKey(v);
    setBusyKey(key);
    setHiddenKeys((prev) => { const n = new Set(prev); n.add(key); return n; });
    try {
      await authFetch("/api/profile/venue-dismissals", {
        method: "POST",
        body: JSON.stringify({ name: v.name || v.display_name, lat: v.lat, lng: v.lng }),
      });
    } catch {}
    setBusyKey(null);
  };
  const pinVenue = async (v) => {
    const key = venueDismissKey(v);
    if (pinnedKeys.has(key)) return;
    setBusyKey(key);
    setPinnedKeys((prev) => { const n = new Set(prev); n.add(key); return n; });
    try {
      await authFetch("/api/profile/pinned", {
        method: "POST",
        body: JSON.stringify({
          name: v.name || v.display_name,
          address: v.full_address,
          lat: v.lat,
          lng: v.lng,
          source: "near-hotel",
        }),
      });
    } catch {}
    setBusyKey(null);
  };
  // Track which venues the user has already added as suggested
  // excursions so the + button can flip to ✓ inline. Keyed on
  // `${name}@${lat},${lng}` so the same venue at different distances
  // (different sections) shares state.
  const [addedKeys, setAddedKeys] = useState(new Set());
  const [adding, setAdding] = useState(null);
  const venueKey = (v) => `${(v.name || v.display_name || "").toLowerCase()}@${v.lat || 0},${v.lng || 0}`;
  // In today-mode, anchor info comes from the response (might be the
  // covering hotel's date, or just today's ISO when there's no hotel).
  // `effectiveTripId` / `effectiveDate` collapse the two call shapes.
  const effectiveTripId = tripId || data?.trip_id;
  const effectiveDate = hotelDate || data?.anchor_date;
  // We CAN attach to any trip + any date — the excursion lands on the
  // resolved anchor day. anchor_segment_id is just informational
  // (which hotel the search hung off, if any).
  const canAddToTrip = !!(effectiveTripId && effectiveDate);
  const addVenueAsExcursion = async (venue) => {
    if (!canAddToTrip) return;
    const key = venueKey(venue);
    if (addedKeys.has(key) || adding === key) return;
    setAdding(key);
    try {
      const notesParts = [
        venue.full_address || venue.display_name,
        venue.note,
        typeof venue.distance_m === "number" ? `${venue.distance_m} m from hotel` : null,
      ].filter(Boolean);
      const r = await authFetch(`/api/trips/${effectiveTripId}/segments`, {
        method: "POST",
        body: JSON.stringify({
          type: "excursion",
          title: venue.name || venue.display_name || "Nearby place",
          start_datetime: `${effectiveDate}T09:00`,
          start_location: venue.full_address || venue.display_name || "",
          notes: notesParts.join(" · "),
          status: "suggested",
        }),
      });
      if (r && r.ok) {
        setAddedKeys((prev) => { const n = new Set(prev); n.add(key); return n; });
        if (onAdded) onAdded();
      }
    } catch {}
    setAdding(null);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const body = todayMode
          ? { trip_id: tripId, today: true }
          : { segment_id: segmentId };
        const r = await authFetch("/api/ai/near-hotel-utilities", {
          method: "POST",
          body: JSON.stringify(body),
        });
        if (cancelled) return;
        if (r && r.ok) {
          const d = await r.json();
          setData(d);
          // Default expansion: ≤2 sections → all expanded so the panel
          // looks the same as before. >2 sections → collapse all
          // except the pinned one so headers fit on a single screen.
          const initial = new Set();
          if ((d.sections || []).length <= 2) {
            for (const s of d.sections || []) initial.add(s.tag_id);
          } else {
            for (const s of d.sections || []) {
              if (s.is_pinned) initial.add(s.tag_id);
            }
          }
          setExpandedSections(initial);
          if (d.reason === "no_tags") setErr("Add some preferences in Profile to use this.");
        } else {
          const d = r ? await r.json().catch(() => ({})) : {};
          setErr(d.error || "Failed to load");
        }
      } catch (e) {
        if (!cancelled) setErr(e?.message || "Failed to load");
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [segmentId, authFetch]);

  return (
    <div style={{
      marginTop: 8, padding: 12,
      background: "rgba(68,138,255,0.05)",
      border: "1px solid rgba(68,138,255,0.25)",
      borderRadius: 8,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <strong style={{ color: "var(--blue)", fontSize: 13 }}>Near my hotel — your preferences</strong>
        {onClose && (
          <button onClick={onClose}
            style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 16 }}>×</button>
        )}
      </div>
      {loading && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Searching nearby venues…</div>}
      {err && <div style={{ fontSize: 12, color: "var(--text-dim)" }}>{err}</div>}
      {!loading && data?.sections?.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {data.sections.map((s) => {
            const sectionRadius = s.radius_m || data.radius_m || 1000;
            const widened = !!s.expanded;
            const visibleVenues = (s.venues || []).filter((v) => !hiddenKeys.has(venueDismissKey(v)));
            const venueCount = visibleVenues.length;
            const isOpen = expandedSections.has(s.tag_id);
            const toggle = () => {
              setExpandedSections((prev) => {
                const next = new Set(prev);
                if (next.has(s.tag_id)) next.delete(s.tag_id);
                else next.add(s.tag_id);
                return next;
              });
            };
            return (
            <div key={s.tag_id}>
              <button
                onClick={toggle}
                style={{
                  width: "100%", background: "none", border: "none", padding: "2px 0",
                  display: "flex", alignItems: "baseline", gap: 6, cursor: "pointer",
                  textAlign: "left", marginBottom: 4,
                }}>
                <span style={{ fontSize: 10, color: "var(--text-dim)", lineHeight: 1 }}>
                  {isOpen ? "▾" : "▸"}
                </span>
                <span style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  {s.kind} · <span style={{ color: "var(--text)", textTransform: "none", letterSpacing: 0 }}>{s.tag}</span>
                </span>
                {venueCount > 0 && (
                  <span style={{ fontSize: 10, color: "var(--text-dim)", marginLeft: 4 }}>
                    {venueCount} nearby
                  </span>
                )}
                {widened && isOpen && (
                  <span style={{ color: "var(--amber)", marginLeft: 4, fontSize: 10 }}>
                    · searched {(sectionRadius / 1000).toFixed(sectionRadius % 1000 === 0 ? 0 : 1)} km
                  </span>
                )}
              </button>
              {isOpen && s.ai_hint && (
                onOpenAiTab ? (
                  <button
                    onClick={(e) => { e.stopPropagation(); onOpenAiTab(s.tag); }}
                    style={{
                      background: "none", border: "none", padding: "0 0 0 4px",
                      fontSize: 11, color: "var(--purple)", fontStyle: "italic",
                      textAlign: "left", cursor: "pointer", textDecoration: "underline",
                      marginBottom: 4,
                    }}>
                    ✨ {s.ai_hint}
                  </button>
                ) : (
                  <div style={{ fontSize: 11, color: "var(--purple)", fontStyle: "italic", marginBottom: 4, paddingLeft: 4 }}>
                    ✨ {s.ai_hint}
                  </div>
                )
              )}
              {!isOpen ? null : (visibleVenues.length || 0) === 0 ? (
                <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic", paddingLeft: 8 }}>
                  {s.osm_thin ? (() => {
                    // Path-style URL forces Google Maps to centre the
                    // map at the supplied lat/lng instead of falling
                    // back to its global "popular result" (which lands
                    // a search for "Ayurvedic" in Barcelona's tourist
                    // district even when we say "near 41.99,2.82").
                    const encodedTag = encodeURIComponent(s.tag);
                    const url = (data.anchor_lat && data.anchor_lng)
                      ? `https://www.google.com/maps/search/${encodedTag}/@${data.anchor_lat},${data.anchor_lng},15z`
                      : `https://www.google.com/maps/search/?api=1&query=${encodedTag}`;
                    return <>
                      OpenStreetMap's coverage is thin for <strong>{s.tag}</strong>.{" "}
                      <a href={url} target="_blank" rel="noreferrer noopener"
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: "var(--blue)" }}>
                        Search Google Maps near your hotel →
                      </a>
                    </>;
                  })() : <>Nothing found within {sectionRadius / 1000} km.</>}
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {visibleVenues.slice(0, 6).map((v, i) => {
                    const name = v.name || v.display_name || "Unnamed";
                    // Open in walking-directions mode FROM the hotel
                    // when we have both coords — otherwise Google
                    // Maps' "Directions" tap defaults origin to the
                    // user's current GPS location, which isn't useful
                    // when planning around a future hotel stay. Falls
                    // back to a labelled pin if we don't have the hotel
                    // coords (older cached responses pre-PR #148).
                    const haveBothCoords = data.anchor_lat && data.anchor_lng && v.lat && v.lng;
                    const mapsUrl = haveBothCoords
                      ? `https://www.google.com/maps/dir/?api=1&origin=${data.anchor_lat},${data.anchor_lng}&destination=${v.lat},${v.lng}&travelmode=walking`
                      : (v.lat && v.lng)
                        ? `https://www.google.com/maps/?q=${v.lat},${v.lng}+(${encodeURIComponent(name)})`
                        : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(name)}`;
                    const vKey = venueKey(v);
                    const isAdded = addedKeys.has(vKey);
                    const isAddingNow = adding === vKey;
                    return (
                      <div key={i} style={{ display: "flex", flexDirection: "column", gap: 2, padding: "5px 8px", background: "var(--card)", borderRadius: 4 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                          <span style={{ color: "var(--text)", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {name}
                          </span>
                          <span style={{ color: "var(--green)", fontFamily: "var(--ff-mono)", flexShrink: 0 }}>
                            {v.distance_m < 1000 ? `${v.distance_m} m` : `${(v.distance_m / 1000).toFixed(1)} km`}
                          </span>
                          <a href={mapsUrl} target="_blank" rel="noreferrer noopener"
                            onClick={(e) => e.stopPropagation()}
                            title="Open in Google Maps"
                            style={{ flexShrink: 0, color: "var(--blue)", textDecoration: "none", fontSize: 14, lineHeight: 1, padding: "0 2px" }}>
                            📍
                          </a>
                          {canAddToTrip && (
                            <button
                              onClick={(e) => { e.stopPropagation(); addVenueAsExcursion(v); }}
                              disabled={isAdded || isAddingNow}
                              title={isAdded ? "Added to trip" : "Add to trip as a suggested excursion"}
                              style={{
                                flexShrink: 0, background: "none", border: "none",
                                color: isAdded ? "var(--green)" : "var(--green)",
                                cursor: isAdded || isAddingNow ? "default" : "pointer",
                                fontSize: 16, lineHeight: 1, padding: "0 2px",
                                opacity: isAddingNow ? 0.5 : 1,
                              }}>
                              {isAdded ? "✓" : "+"}
                            </button>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); pinVenue(v); }}
                            disabled={pinnedKeys.has(venueDismissKey(v)) || busyKey === venueDismissKey(v)}
                            title={pinnedKeys.has(venueDismissKey(v)) ? "Pinned to favourites" : "Pin to favourites (cross-trip)"}
                            style={{
                              flexShrink: 0, background: "none", border: "none",
                              color: pinnedKeys.has(venueDismissKey(v)) ? "var(--amber)" : "var(--text-dim)",
                              cursor: "pointer", fontSize: 12, lineHeight: 1, padding: "0 2px",
                            }}>
                            📌
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm(`Hide "${name}" from your "Near my hotel" suggestions across all trips?`)) hideVenue(v);
                            }}
                            disabled={busyKey === venueDismissKey(v)}
                            title="Don't show this venue again"
                            style={{
                              flexShrink: 0, background: "none", border: "none",
                              color: "var(--red)", cursor: "pointer",
                              fontSize: 13, lineHeight: 1, padding: "0 2px", opacity: 0.6,
                            }}>
                            ✕
                          </button>
                        </div>
                        {v.note && (
                          <div style={{ fontSize: 11, color: "var(--text-dim)", fontStyle: "italic", lineHeight: 1.35 }}>
                            {v.note}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
      {!loading && !err && data?.sections?.length === 0 && data?.reason !== "no_tags" && (
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>No preferences saved yet.</div>
      )}
    </div>
  );
}
